import {
  useState,
  useEffect,
  useLayoutEffect,
  MouseEvent,
  useCallback,
  useRef,
} from 'react';

import { debounce, isElementVisible } from '../01-core/Utilities';
import { beginProgrammaticScroll } from '../01-core/ProgrammaticScroll';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export interface AnchorNavigationItemProps {
  /** The label */
  label: string;
  /** The name of the anchor */
  anchor: string | HTMLElement;
  /** The optional title */
  title?: string;
  /** Optional extra class(es) for the list item, e.g. viewport visibility */
  className?: string;
}

export interface AnchorNavigationProps
  extends React.HTMLAttributes<HTMLElement> {
  /** The navigation items */
  items?: AnchorNavigationItemProps[];
  /** Optional style */
  style?: React.CSSProperties;
}

/** A block rendering the anchor navigation */
export const AnchorNavigation = ({
  items = [],
  ...props
}: AnchorNavigationProps) => {
  const ref = useRef<HTMLElement>(null);
  const nav = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState<null | number>(null);
  // Mirrors `active` for the scroll listener: reading the state through the
  // closure would tie the listener's identity to every active change.
  const activeIndex = useRef<null | number>(null);

  const findTarget = useCallback((anchor: string | HTMLElement) => {
    if (anchor instanceof HTMLElement) {
      return anchor;
    }
    let target: HTMLElement | null = document.getElementById(anchor);
    if (!target) {
      const targets: NodeListOf<HTMLElement> = document.querySelectorAll(
        'a[name="' + anchor + '"]',
      );
      if (!targets) {
        return null;
      }
      target = targets[0];
    }
    return target;
  }, []);

  const checkActive = useCallback(() => {
    if (!nav.current) return;
    let scrollTo: HTMLElement | null = null;
    let update: number | null = null;
    items?.forEach((item, i) => {
      const target = findTarget(item.anchor);
      if (target && isElementVisible(target, 2)) {
        update = i;
        scrollTo =
          (ref.current?.getElementsByClassName('anchor-navigation__nav-item')[
            i
          ] as HTMLElement) || scrollTo;
      }
    });
    if (update !== null && update !== activeIndex.current) {
      activeIndex.current = update;
      setActive(update);
      if (scrollTo !== null) {
        scrollTo = scrollTo as HTMLElement;
        const computed = window.getComputedStyle(nav.current);
        const left =
          scrollTo.offsetLeft -
          parseInt(computed.getPropertyValue('padding-left') || '0');
        const right =
          scrollTo.offsetLeft +
          scrollTo.offsetWidth +
          parseInt(computed.getPropertyValue('padding-right') || '0');
        const curRight = nav.current.scrollLeft + nav.current.offsetWidth;
        const curLeft = nav.current.scrollLeft;

        if (left < curLeft) {
          nav.current.scrollTo({ behavior: 'smooth', left: left });
        } else if (right > curRight) {
          nav.current.scrollTo({
            behavior: 'smooth',
            left: right - nav.current.offsetWidth,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, ref, findTarget]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkOverflow = useCallback(
    debounce(
      () => {
        if (!ref.current || !nav.current) return;
        const wrapperWidth = ref.current.getBoundingClientRect().width;
        // scrollWidth instead of measuring the last item: the last item may be
        // display:none (viewport-restricted anchor items) and would report a
        // zero offset, wrongly disabling the overflow indicators.
        const navWidth = nav.current.scrollWidth;
        const scrollLeft = nav.current.scrollLeft;
        ref.current.classList.toggle(
          'anchor-navigation--overflow-left',
          scrollLeft > 0,
        );
        ref.current.classList.toggle(
          'anchor-navigation--overflow-right',
          navWidth - (scrollLeft + wrapperWidth) > 1,
        );
      },
      100,
      false,
    ),
    [ref, nav],
  );

  const onResize = useCallback(() => {
    if (!ref.current) return;
    const height = ref.current.getBoundingClientRect().height;
    document
      .getElementsByTagName('body')[0]
      .style.setProperty(
        '--anchor-navigation--height',
        Math.floor(height) + 'px',
      );
  }, [ref]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    debounce(
      () => {
        onResize();
      },
      100,
      false,
    ),
    [onResize],
  ); // some debounce typings expect the 3rd arg

  // Measured in a layout effect rather than in an animation frame: a hash deep
  // link scrolls from a frame callback too, and `scroll-margin-top` would then
  // still be computed with a height of 0 — the first jump landed too high while
  // a second attempt on the same anchor was correct (#1778).
  useIsomorphicLayoutEffect(() => {
    onResize();
    return () => {
      document
        .getElementsByTagName('body')[0]
        .style.removeProperty('--anchor-navigation--height');
    };
  }, [onResize]);

  useEffect(() => {
    const tmpNav = nav.current;
    window.addEventListener(
      'scroll',
      checkActive as EventListener,
      { passive: true } as AddEventListenerOptions,
    );
    tmpNav?.addEventListener(
      'scroll',
      checkOverflow as EventListener,
      { passive: true } as AddEventListenerOptions,
    );
    window.addEventListener('resize', handleResize as unknown as EventListener);
    window.requestAnimationFrame(() => {
      checkActive();
      checkOverflow();
    });
    return () => {
      window.removeEventListener('scroll', checkActive as EventListener);
      tmpNav?.removeEventListener('scroll', checkOverflow as EventListener);
      window.removeEventListener(
        'resize',
        handleResize as unknown as EventListener,
      );
    };
  }, [checkActive, checkOverflow, handleResize]);

  const scrollTo = (
    e: MouseEvent<HTMLAnchorElement>,
    item: AnchorNavigationItemProps,
  ) => {
    e.preventDefault();
    const target = findTarget(item.anchor);
    if (target) {
      // Announce it first: otherwise the header reads the jump as a downward
      // user scroll and pushes this very navigation off-screen (#1778).
      beginProgrammaticScroll();
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <nav ref={ref} className="anchor-navigation" {...props}>
      <ul ref={nav} className="anchor-navigation__nav">
        {items.map((item, i) => {
          return (
            <li
              key={i}
              className={[
                'anchor-navigation__nav-item',
                i == active ? 'anchor-navigation__nav-item--active' : '',
                item.className || '',
              ].join(' ')}
            >
              <a
                className="anchor-navigation__nav-link"
                onClick={(e) => scrollTo(e, item)}
                href={'#' + item.anchor}
                title={item.title}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
